import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as reportsApi from '../api/reports';

// Async thunk to fetch all dashboard data
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchAll',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportsApi.fetchAllDashboardData(filters);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch overview
export const fetchOverview = createAsyncThunk(
  'dashboard/fetchOverview',
  async (_, { rejectWithValue }) => {
    try {
      const response = await reportsApi.getOverview();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch season coverage
export const fetchSeasonCoverage = createAsyncThunk(
  'dashboard/fetchSeasonCoverage',
  async (_, { rejectWithValue }) => {
    try {
      const response = await reportsApi.getCoverageBySeason();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch category coverage
export const fetchCategoryCoverage = createAsyncThunk(
  'dashboard/fetchCategoryCoverage',
  async (_, { rejectWithValue }) => {
    try {
      const response = await reportsApi.getCoverageByCategory();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch missing images
export const fetchMissingImages = createAsyncThunk(
  'dashboard/fetchMissingImages',
  async (params, { rejectWithValue }) => {
    try {
      const response = await reportsApi.getMissingImages(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch priority items
export const fetchPriorityItems = createAsyncThunk(
  'dashboard/fetchPriorityItems',
  async (limit, { rejectWithValue }) => {
    try {
      const response = await reportsApi.getPriorityItems(limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch recent updates
export const fetchRecentUpdates = createAsyncThunk(
  'dashboard/fetchRecentUpdates',
  async (limit, { rejectWithValue }) => {
    try {
      const response = await reportsApi.getRecentUpdates(limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  overview: null,
  seasonCoverage: [],
  categoryCoverage: [],
  collectionCoverage: [],
  timeline: [],
  recentUpdates: [],
  priorityItems: [],
  missingImages: [],
  qualityMetrics: null,
  loading: false,
  error: null,
  lastUpdated: null
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetDashboard: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Fetch all dashboard data
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload.overview.data;
        state.seasonCoverage = action.payload.seasonCoverage.data;
        state.categoryCoverage = action.payload.categoryCoverage.data;
        state.collectionCoverage = action.payload.collectionCoverage.data;
        state.timeline = action.payload.timeline.data;
        state.recentUpdates = action.payload.recentUpdates.data;
        state.priorityItems = action.payload.priorityItems.data;
        state.qualityMetrics = action.payload.qualityMetrics.data;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch overview
      .addCase(fetchOverview.fulfilled, (state, action) => {
        state.overview = action.payload;
      })
      // Fetch season coverage
      .addCase(fetchSeasonCoverage.fulfilled, (state, action) => {
        state.seasonCoverage = action.payload;
      })
      // Fetch category coverage
      .addCase(fetchCategoryCoverage.fulfilled, (state, action) => {
        state.categoryCoverage = action.payload;
      })
      // Fetch missing images
      .addCase(fetchMissingImages.fulfilled, (state, action) => {
        state.missingImages = action.payload.items || action.payload;
      })
      // Fetch priority items
      .addCase(fetchPriorityItems.fulfilled, (state, action) => {
        state.priorityItems = action.payload;
      })
      // Fetch recent updates
      .addCase(fetchRecentUpdates.fulfilled, (state, action) => {
        state.recentUpdates = action.payload;
      });
  }
});

export const { clearError, resetDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
